import { getApiConfig } from "@/config/api.config";
import { BaseApiService } from "@/services/api/BaseApiService";
import { CoreService } from "@/services/core/CoreService";
import { getCurrentRegion, Region } from "@/utils/environment";

export interface Job {
  id: string;
  name: string;
  customerId: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | number;
  instanceId?: string;
  scheduleName?: string;
  reportPath?: string;
  dataPath?: string;
  report?: unknown;
}

export interface JobStatus {
  status: string;
  createdAt: string;
}

export interface JobDetails {
  job: Job & {
    completedAt?: string | number;
    config?: string;
  };
  statuses: JobStatus[];
  isCompleted: boolean;
  isSuccessful: boolean;
}

export interface JobResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface EtlEnvironmentConfig {
  projectId: string;
  schedulerEnvironment: "dev" | "na" | "eu";
  storageBucket: string;
}

export const getEtlEnvironmentConfig = (
  region: Region = getCurrentRegion(),
): EtlEnvironmentConfig => {
  switch (region) {
    case Region.DEV:
      return {
        projectId: "craveretail-dev",
        schedulerEnvironment: "dev",
        storageBucket: "crave-etl-dev",
      };
    case Region.EMEA:
      return {
        projectId: "craveretail-prod",
        schedulerEnvironment: "eu",
        storageBucket: "crave-etl-prod",
      };
    case Region.NA:
    default:
      return {
        projectId: "craveretail-prod",
        schedulerEnvironment: "na",
        storageBucket: "crave-etl-prod",
      };
  }
};

export class JobService extends BaseApiService {
  private static instance: JobService;
  protected readonly coreService: CoreService;

  private constructor() {
    super();
    this.coreService = CoreService.getInstance();
  }

  public static getInstance(): JobService {
    if (!JobService.instance) {
      JobService.instance = new JobService();
    }
    return JobService.instance;
  }

  private getCoreBaseUrl(): string {
    return getApiConfig().core;
  }

  public async getJobList(
    customerId?: number,
    jobName?: string,
    status?: string,
  ): Promise<JobResponse<Job[]>> {
    try {
      const queryParams = new URLSearchParams();

      if (customerId === 0) {
        queryParams.append("global", "true");
      } else if (customerId) {
        queryParams.append("customerId", customerId.toString());
      }

      if (jobName) {
        queryParams.append("jobName", jobName);
      }

      if (status) {
        queryParams.append("status", status);
      }

      const response = await this.get<{ data: Job[] }>(
        `/jobs/customer${queryParams.toString() ? "?" + queryParams.toString() : ""}`,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch jobs",
      };
    }
  }

  public async getJobNames(): Promise<JobResponse<string[]>> {
    try {
      const response = await this.get<{ data: string[] }>("/jobs/names", this.getCoreBaseUrl());
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch job names",
      };
    }
  }

  public async getJobDetails(jobId: string): Promise<JobResponse<JobDetails>> {
    try {
      const response = await this.get<{ data: JobDetails }>(
        `/job/id/${jobId}`,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch job details",
      };
    }
  }

  public async runJobManually(scheduleName: string): Promise<JobResponse<void>> {
    try {
      const authHeader = this.coreService.getAuthorizationHeader();

      if (!authHeader) {
        throw new Error("You must be logged in to access this resource");
      }

      const token = authHeader.startsWith("Bearer ") ? authHeader : `Bearer ${authHeader}`;
      const environmentConfig = getEtlEnvironmentConfig();
      const response = await fetch(
        `https://us-central1-${environmentConfig.projectId}.cloudfunctions.net/RunScheduledJobFromHTTP`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            authorization: token,
          },
          body: JSON.stringify({
            scheduleName,
            environment: environmentConfig.schedulerEnvironment,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to run job manually: ${response.status}`);
      }

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to run job manually",
      };
    }
  }
}
